import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useRazorpay } from "react-razorpay";
import { toast } from "sonner";
import { ShieldCheck, Banknote, Smartphone, CreditCard, ArrowLeft, ChevronRight } from "lucide-react";
import { API, inr, fmtErr } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";

const EMPTY_ADDR = { name: "", phone: "", line1: "", line2: "", city: "", state: "", pincode: "" };

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { Razorpay } = useRazorpay();
  const [addr, setAddr] = useState(EMPTY_ADDR);
  const [email, setEmail] = useState("");
  const [method, setMethod] = useState("upi");
  const [step, setStep] = useState("details");
  const [coupon] = useState(location.state?.coupon || null);
  const [useLoyalty, setUseLoyalty] = useState(!!location.state?.loyalty);
  const [placing, setPlacing] = useState(false);

  useEffect(() => {
    if (user) {
      setEmail(user.email);
      setAddr((a) => ({ ...a, name: user.name }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const stamps = user?.stamps || 0;
  const loyalty = useLoyalty && stamps >= 5 ? Math.round(subtotal * 0.4) : 0;
  const discount = coupon?.discount || 0;
  const shipping = subtotal - discount - loyalty >= 1499 || items.length === 0 ? 0 : 99;
  const total = subtotal - discount - loyalty + shipping;

  if (items.length === 0)
    return (
      <div className="max-w-3xl mx-auto px-4 py-32 text-center" data-testid="checkout-empty">
        <h1 className="font-display font-black text-3xl">Nothing to check out</h1>
        <Link to="/shop" className="text-gold hover:underline mt-4 inline-block">Back to shop</Link>
      </div>
    );

  const set = (k) => (e) => setAddr({ ...addr, [k]: e.target.value });

  const validate = () => {
    if (!email.includes("@")) return "Enter a valid email";
    for (const k of ["name", "phone", "line1", "city", "state", "pincode"])
      if (!addr[k].trim()) return "Please fill all address fields";
    if (addr.phone.replace(/\D/g, "").length < 10) return "Enter a valid phone number";
    return null;
  };

  const placeOrder = async () => {
    const err = validate();
    if (err) return toast.error(err);
    setPlacing(true);
    try {
      const payload = {
        items: items.map(({ product_id, size, color, qty }) => ({ product_id, size, color, qty })),
        address: addr, email, coupon_code: coupon?.code || null, payment_method: method, use_loyalty: loyalty > 0,
      };
      const r = await API.post("/orders", payload);
      if (method === "cod") {
        clear();
        navigate(`/order-success/${r.data.id}`);
        return;
      }
      const order = r.data;
      const rzp = new Razorpay({
        key: order.razorpay_key_id,
        amount: order.razorpay_order.amount,
        currency: "INR",
        name: "Dressedin by Urbanz",
        description: `Order ${order.order_number}`,
        order_id: order.razorpay_order.id,
        theme: { color: "#D4AF37", backdrop_color: "#0A0A0A" },
        prefill: { name: addr.name, email, contact: addr.phone },
        method: method === "upi"
          ? { upi: true, card: false, netbanking: false, wallet: false }
          : { upi: false, card: true, netbanking: false, wallet: false },
        handler: async (resp) => {
          try {
            await API.post("/orders/verify-payment", {
              order_id: order.id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            clear();
            navigate(`/order-success/${order.id}`);
          } catch (e) {
            toast.error(fmtErr(e, "Payment verification failed"));
          }
        },
        modal: { ondismiss: () => toast.error("Payment cancelled — your order is saved as unpaid") },
      });
      rzp.open();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setPlacing(false);
    }
  };

  const inputCls = "w-full bg-ink border border-[#262626] px-4 py-3 text-sm outline-none focus:border-gold transition-colors";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10" data-testid="checkout-page">
      <h1 className="font-display font-black text-4xl tracking-tighter mb-4">Checkout</h1>
      <div className="flex items-center gap-2 mb-10 text-[11px] uppercase tracking-[0.2em]" data-testid="checkout-steps">
        <span className={step === "details" ? "text-gold font-bold" : "text-muted-foreground"}>1 · Shipping</span>
        <ChevronRight size={13} className="text-muted-foreground" />
        <span className={step === "payment" ? "text-gold font-bold" : "text-muted-foreground"}>2 · Payment</span>
      </div>
      <div className="grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-8">
          {step === "details" && (<>
          <section className="border border-[#262626] bg-ink-surface p-6">
            <h2 className="font-display font-bold text-lg mb-5">Contact</h2>
            <input data-testid="checkout-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="Email address" className={inputCls} />
          </section>

          <section className="border border-[#262626] bg-ink-surface p-6">
            <h2 className="font-display font-bold text-lg mb-5">Shipping address</h2>
            {user && (user.addresses || []).length > 0 && (
              <div className="flex flex-wrap gap-2 mb-5">
                {user.addresses.map((a) => (
                  <button key={a.id} onClick={() => setAddr({ ...EMPTY_ADDR, ...a })} data-testid={`saved-address-${a.id}`}
                    className="text-xs border border-[#262626] px-3 py-2 hover:border-gold transition-colors text-muted-foreground">
                    {a.label}: {a.line1}, {a.city}
                  </button>
                ))}
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-4">
              <input data-testid="addr-name" value={addr.name} onChange={set("name")} placeholder="Full name" className={inputCls} />
              <input data-testid="addr-phone" value={addr.phone} onChange={set("phone")} placeholder="Phone" className={inputCls} />
              <input data-testid="addr-line1" value={addr.line1} onChange={set("line1")} placeholder="Address line 1" className={`${inputCls} sm:col-span-2`} />
              <input data-testid="addr-line2" value={addr.line2} onChange={set("line2")} placeholder="Address line 2 (optional)" className={`${inputCls} sm:col-span-2`} />
              <input data-testid="addr-city" value={addr.city} onChange={set("city")} placeholder="City" className={inputCls} />
              <input data-testid="addr-state" value={addr.state} onChange={set("state")} placeholder="State" className={inputCls} />
              <input data-testid="addr-pincode" value={addr.pincode} onChange={set("pincode")} placeholder="Pincode" className={inputCls} />
            </div>
          </section>
          </>)}

          {step === "payment" && (
            <section className="border border-[#262626] bg-ink-surface p-6" data-testid="payment-screen">
              <div className="flex items-center justify-between mb-5">
                <h2 className="font-display font-bold text-lg">Payment options</h2>
                <button onClick={() => setStep("details")} data-testid="back-to-details-btn"
                  className="text-xs text-muted-foreground hover:text-gold transition-colors flex items-center gap-1">
                  <ArrowLeft size={13} /> Edit details
                </button>
              </div>
              <div className="space-y-3">
                {[
                  { key: "upi", label: "UPI", desc: "Google Pay, PhonePe, Paytm & more — via Razorpay", icon: Smartphone, testid: "pay-upi" },
                  { key: "card", label: "Credit / Debit Card", desc: "Visa, Mastercard, RuPay & more — via Razorpay", icon: CreditCard, testid: "pay-card" },
                  { key: "cod", label: "Cash on Delivery", desc: "Pay when your order arrives at your doorstep", icon: Banknote, testid: "pay-cod" },
                ].map(({ key, label, desc, icon: Icon, testid }) => (
                  <button key={key} type="button" onClick={() => setMethod(key)} data-testid={testid}
                    className={`w-full flex items-center gap-4 border p-4 text-left transition-colors ${method === key ? "border-gold bg-gold/5" : "border-[#262626] hover:border-gold/50"}`}>
                    <Icon size={20} className="text-gold shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-bold">{label}</p>
                      <p className="text-xs text-muted-foreground">{desc}</p>
                    </div>
                    <div className={`h-4 w-4 rounded-full border-2 transition-colors ${method === key ? "border-gold bg-gold" : "border-[#262626]"}`} />
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground mt-4 flex items-center gap-1.5">
                <ShieldCheck size={12} className="text-gold" /> Online payments are processed securely by Razorpay (UPI, cards, net banking, wallets).
              </p>
            </section>
          )}
        </div>

        <div className="border border-[#262626] bg-ink-surface p-6 h-fit lg:sticky lg:top-28" data-testid="checkout-summary">
          <h2 className="font-display font-bold text-lg mb-5">Your order</h2>
          {user && stamps >= 5 && (
            <label className="flex items-center gap-2 text-xs border border-gold/40 p-3 mb-4 cursor-pointer" data-testid="checkout-redeem-label">
              <input type="checkbox" checked={useLoyalty} onChange={(e) => setUseLoyalty(e.target.checked)} className="accent-[#D4AF37]" data-testid="checkout-redeem-checkbox" />
              Use 5 stamps for <b className="text-gold">40% off</b> ({Math.min(stamps, 5)}/5 collected)
            </label>
          )}
          {user && stamps < 5 && (
            <p className="text-[11px] text-muted-foreground border border-[#262626] p-3 mb-4" data-testid="checkout-stamp-progress">
              Stamp card: {stamps}/5 — earn 1 per order, redeem 5 for 40% off.
            </p>
          )}
          <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
            {items.map((i) => (
              <div key={`${i.product_id}|${i.size}|${i.color}`} className="flex gap-3 items-center">
                <img src={i.image} alt="" className="h-14 w-11 object-cover" loading="lazy" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold truncate">{i.name}</p>
                  <p className="text-[10px] text-muted-foreground">{i.size}/{i.color} × {i.qty}</p>
                </div>
                <p className="text-xs font-bold text-gold">{inr(i.price * i.qty)}</p>
              </div>
            ))}
          </div>
          <div className="space-y-2 text-sm border-t border-[#262626] mt-5 pt-4">
            <div className="flex justify-between text-muted-foreground"><span>Subtotal</span><span>{inr(subtotal)}</span></div>
            {discount > 0 && <div className="flex justify-between text-gold"><span>Coupon {coupon.code}</span><span>-{inr(discount)}</span></div>}
            {loyalty > 0 && <div className="flex justify-between text-gold"><span>Stamp card (40%)</span><span data-testid="checkout-loyalty-discount">-{inr(loyalty)}</span></div>}
            <div className="flex justify-between text-muted-foreground"><span>Shipping</span><span>{shipping === 0 ? "FREE" : inr(shipping)}</span></div>
            <div className="flex justify-between font-display font-black text-lg pt-3 border-t border-[#262626]">
              <span>Total</span><span className="gold-text" data-testid="checkout-total">{inr(total)}</span>
            </div>
          </div>
          {step === "details" ? (
            <button onClick={() => { const err = validate(); if (err) return toast.error(err); setStep("payment"); }}
              data-testid="continue-to-payment-btn"
              className="btn-gold w-full mt-6 py-4 font-display font-bold text-sm uppercase tracking-[0.2em]">
              Continue to payment
            </button>
          ) : (
            <button onClick={placeOrder} disabled={placing} data-testid="place-order-btn"
              className="btn-gold w-full mt-6 py-4 font-display font-bold text-sm uppercase tracking-[0.2em] disabled:opacity-50">
              {placing ? "Processing..." : method === "cod" ? "Place order (COD)" : `Pay ${inr(total)} via ${method.toUpperCase()}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
